/* eslint-disable no-param-reassign */
import produce from 'immer';
import _ from 'lodash';
import {
  GET_CONTACTS,
  GET_THREADS,
  GET_THREAD,
  MARK_THREAD_AS_SEEN,
  ADD_MESSAGE,
  UNREAD_COUNT,
  SET_SORT,
  UPDATE_MESSAGE,
  UPDATE_MESSAGES,
  OPEN_SIDEBAR,
  CLOSE_SIDEBAR,
  PAUSE_THREAD,
  GET_MESSAGES,
  ADD_THREAD,
  GET_HANDOFFS,
  RESOLVE_HANDOFF,
  UPDATE_HANDOFF,
  ADD_HANDOFF,
  GET_HANDOFF_COMMENTS,
  GET_THREAD_COMMENTS,
  ADD_HANDOFF_COMMENTS,
  UPDATE_HANDOFF_COMMENTS,
  DELETE_HANDOFF_COMMENTS,
  ADD_HANDOFF_PAUSE_THREAD,
  GET_TEMPLATES,
  GET_ACTION_LOGS
} from 'src/actions/chatActions';
import { ADD_PENDING_MESSAGE } from 'src/actions/channelWeb';
import moment from 'moment';
import objFromArray from 'src/utils/objFromArray';

const initialState = {
  contacts: {
    byId: {},
    allIds: []
  },
  templates: {
    byId: {},
    allIds: []
  },
  threads: {
    byKey: {},
    allKeys: []
  },
  handoffs: {
    byKey: {},
    allKeys: []
  },
  handoffComments: {
    byHandoffId: {},
    byThreadId: {}
  },
  messages: {
    byThreadId: {}
  },
  actionLogs: [],
  sidebarOpen: true,
  sort: 'last_seen|desc'
};

const chatReducer = (state = initialState, action) => {
  switch (action.type) {
    //ActionsLogs
    case GET_ACTION_LOGS: {
      const { logs } = action.payload;

      return produce(state, draft => {
        draft.actionLogs = logs;
      });
    }
    //Contacts
    case GET_CONTACTS: {
      const { users } = action.payload;

      return produce(state, draft => {
        draft.contacts.byId = objFromArray(users);
        draft.contacts.allIds = Object.keys(draft.contacts.byId);
      });
    }
    //Templates
    case GET_TEMPLATES: {
      const { templates } = action.payload;
      return produce(state, draft => {
        draft.templates.byId = objFromArray(templates, 'name');
        draft.templates.allIds = Object.keys(draft.templates.byId);
      });
    }
    //Threads
    case GET_THREADS: {
      const { threads } = action.payload;

      return produce(state, draft => {
        draft.threads.byKey = {
          ...draft.threads.byKey,
          ...objFromArray(threads, 'id')
        };
        draft.threads.allKeys = Object.keys(draft.threads.byKey);
      });
    }

    case GET_THREAD: {
      const { thread } = action.payload;

      return produce(state, draft => {
        draft.threads.byKey[thread.id] = thread;

        if (!draft.threads.allKeys.includes(thread.id)) {
          draft.threads.allKeys.push(thread.id);
        }
      });
    }

    case ADD_THREAD: {
      const { thread } = action.payload;

      return produce(state, draft => {
        draft.threads.byKey[thread.id] = thread;
        if (!draft.threads.allKeys.includes(thread.id)) {
          draft.threads.allKeys.push(thread.id);
        }
      });
    }

    case MARK_THREAD_AS_SEEN: {
      const { thread_id } = action.payload;

      return produce(state, draft => {
        const thread = draft.threads.byKey[thread_id];

        if (thread) {
          thread.unread_count = 0;
        }
      });
    }
    case PAUSE_THREAD: {
      const { contactId } = action.payload;

      return produce(state, draft => {
        const contact = draft.contacts.byId[contactId];

        if (contact) {
          contact.isActive = !contact.isActive;
        }
      });
    }
    case UNREAD_COUNT: {
      const { threadId } = action.payload;

      return produce(state, draft => {
        if (draft.threads.allKeys.includes(threadId)) {
          draft.threads.byKey[threadId] = {
            ...draft.threads.byKey[threadId],
            unread_count: draft.threads.byKey[threadId].unread_count + 1
          };
        }

        draft.handoffs.allKeys.map(handoffId => {
          const handoff = draft.handoffs.byKey[handoffId];
          if (handoff.thread_id === threadId) {
            draft.handoffs.byKey[handoffId] = {
              ...handoff,
              last_seen: moment().toISOString()
            };
          }
        });
      });
    }
    //Messages
    case GET_MESSAGES: {
      const { threadId, messages } = action.payload;

      return produce(state, draft => {
        draft.messages.byThreadId[threadId] = messages;
      });
    }

    case ADD_MESSAGE: {
      const { message } = action.payload;
      return produce(state, draft => {
        if (!draft.messages.byThreadId[message.thread_id]) {
          draft.messages.byThreadId[message.thread_id] = [message];
        } else {
          let filteredMessages = _.reject(
            draft.messages.byThreadId[message.thread_id],
            'pending'
          );
          filteredMessages.push(message);
          draft.messages.byThreadId[message.thread_id] = filteredMessages;
        }
      });
    }
    case ADD_PENDING_MESSAGE: {
      const { message } = action.payload;
      return produce(state, draft => {
        if (!draft.messages.byThreadId[message.thread_id]) {
          draft.messages.byThreadId[message.thread_id] = [message];
        } else {
          draft.messages.byThreadId[message.thread_id].push(message);
        }
      });
    }

    case UPDATE_MESSAGE: {
      const { message } = action.payload;
      return produce(state, draft => {
        if (!draft.messages.byThreadId[message.thread_id]) {
          draft.messages.byThreadId[message.thread_id] = [message];
        } else {
          const thread = draft.messages.byThreadId[message.thread_id];
          const idx = thread.findIndex(msg => msg.id === message.id);
          if (idx !== -1) {
            thread[idx] = message;
          } else {
            draft.messages.byThreadId[message.thread_id].push(message);
          }
        }
      });
    }

    case UPDATE_MESSAGES: {
      const { messages } = action.payload;
      return produce(state, draft => {
        if (!messages?.length) {
          return;
        }
        if (!draft.messages.byThreadId) {
          draft.messages.byThreadId[messages[0].thread_id] = messages;
        } else {
          draft.messages.byThreadId[messages[0].thread_id].push(...messages);
        }
      });
    }

    //SIDEBAR
    case OPEN_SIDEBAR: {
      return produce(state, draft => {
        draft.sidebarOpen = true;
      });
    }

    case SET_SORT: {
      const { sort } = action.payload;
      return produce(state, draft => {
        draft.sort = sort;
      });
    }

    case CLOSE_SIDEBAR: {
      return produce(state, draft => {
        draft.sidebarOpen = false;
      });
    }

    //HANDOFF
    case GET_HANDOFFS: {
      const { handoffs, threads } = action.payload;

      return produce(state, draft => {
        draft.handoffs.byKey = objFromArray(handoffs, 'id');
        draft.handoffs.allKeys = Object.keys(draft.handoffs.byKey);
        draft.threads.byKey = {
          ...draft.threads.byKey,
          ...objFromArray(threads, 'id')
        };
        draft.threads.allKeys = Object.keys(draft.threads.byKey);
      });
    }

    case ADD_HANDOFF: {
      const { handoff } = action.payload;
      const handoffId = handoff.id.toString();
      return produce(state, draft => {
        draft.handoffs.byKey[handoffId] = handoff;
        if (!draft.handoffs.allKeys.includes(handoffId)) {
          draft.handoffs.allKeys.push(handoffId);
        }
      });
    }

    case ADD_HANDOFF_PAUSE_THREAD: {
      const { thread } = action.payload;
      return produce(state, draft => {
        let pausedThread = {
          ...thread,
          paused: true
        };
        draft.threads.byKey[thread.id] = pausedThread;
        if (!draft.threads.allKeys.includes(thread.id)) {
          draft.threads.allKeys.push(thread.id);
        }
      });
    }

    case UPDATE_HANDOFF: {
      const { handoff } = action.payload;
      const handoffId = handoff.id.toString();
      return produce(state, draft => {
        draft.handoffs.byKey[handoffId] = handoff;
        if (!draft.handoffs.allKeys.includes(handoffId)) {
          draft.handoffs.allKeys.push(handoffId);
        }
      });
    }
    case RESOLVE_HANDOFF: {
      const { handoff } = action.payload;
      const handoffId = handoff.id.toString();
      return produce(state, draft => {
        let hdf = draft.handoffs.byKey[handoffId];
        hdf.resolved_at = Date.now();
        draft.handoffs.byKey[handoffId] = hdf;
        if (!draft.handoffs.allKeys.includes(handoffId)) {
          draft.handoffs.allKeys.push(handoffId);
        }
      });
    }

    //Handoff comments

    case GET_HANDOFF_COMMENTS: {
      const { handoffId, comments } = action.payload;
      return produce(state, draft => {
        draft.handoffComments.byHandoffId[handoffId] = comments;
      });
    }
    case GET_THREAD_COMMENTS: {
      const { threadId, comments } = action.payload;
      return produce(state, draft => {
        draft.handoffComments.byThreadId[threadId] = comments;
      });
    }

    case ADD_HANDOFF_COMMENTS: {
      const { comment, threadKey } = action.payload;
      return produce(state, draft => {
        if (!draft.handoffComments.byHandoffId[comment.handoff_id]) {
          draft.handoffComments.byHandoffId[comment.handoff_id] = [comment];
        } else {
          draft.handoffComments.byHandoffId[comment.handoff_id].push(comment);
        }
        if (!draft.handoffComments.byThreadId[threadKey]) {
          draft.handoffComments.byThreadId[threadKey] = [comment];
        } else {
          draft.handoffComments.byThreadId[threadKey].push(comment);
        }
      });
    }

    case UPDATE_HANDOFF_COMMENTS: {
      const { comment } = action.payload;
      return produce(state, draft => {
        draft.byHandoffId[comment.id] = comment;
        if (!draft.byHandoffId.includes(comment.id)) {
          draft.byHandoffId.push(comment.id);
        }
      });
    }

    case DELETE_HANDOFF_COMMENTS: {
      const { id } = action.payload;

      return produce(state, draft => {
        draft.byHandoffId = _.omit(draft.byHandoffId, id);
        _.pull(draft.handoffId, id);
      });
    }

    default: {
      return state;
    }
  }
};

export default chatReducer;
